import React, { useMemo, useState } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';

const DomainMultiSelect = ({ selectedIds = [], onChange, domainAssets = [] }) => {
    const [search, setSearch] = useState('');

    const selectedSet = useMemo(() => new Set(selectedIds.map(String)), [selectedIds]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return domainAssets;
        return domainAssets.filter((d) => {
            const url = (d.domainURL || '').toLowerCase();
            const status = (d.status || '').toLowerCase();
            const dns = (d.DNS || '').toLowerCase();
            return url.includes(term) || status.includes(term) || dns.includes(term);
        });
    }, [domainAssets, search]);

    const toggle = (domainId) => {
        const id = String(domainId);
        if (selectedSet.has(id)) {
            onChange(selectedIds.filter((v) => String(v) !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    const removeSelected = (domainId) => {
        onChange(selectedIds.filter((v) => String(v) !== String(domainId)));
    };

    const selectedDomains = domainAssets.filter((d) => selectedSet.has(String(d._id)));

    return (
        <div className="space-y-2">
            {selectedDomains.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selectedDomains.map((domain) => (
                        <span
                            key={domain._id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-800 text-xs border border-blue-200"
                        >
                            {domain.domainURL || domain._id}
                            <button
                                type="button"
                                onClick={() => removeSelected(domain._id)}
                                className="text-blue-600 hover:text-blue-900"
                                aria-label="Remove domain"
                            >
                                <FiX className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div className="relative">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search domain name..."
                    className="w-full pl-3 pr-9 py-2 border border-gray-300 rounded-md text-sm text-blue-700"
                />
                <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>

            {domainAssets.length === 0 ? (
                <p className="text-sm text-gray-500">No domain assets available yet.</p>
            ) : (
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1 bg-gray-50">
                    {filtered.length === 0 ? (
                        <p className="text-sm text-gray-500 px-1 py-2">No domains match your search.</p>
                    ) : (
                        filtered.map((domain) => (
                            <label
                                key={domain._id}
                                className="flex items-center gap-2 text-sm text-gray-800 px-1 py-1 hover:bg-white rounded cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedSet.has(String(domain._id))}
                                    onChange={() => toggle(domain._id)}
                                    className="rounded border-gray-300"
                                />
                                <span className="min-w-0 truncate">
                                    {domain.domainURL || domain._id}
                                    {domain.status ? ` (${domain.status})` : ''}
                                </span>
                            </label>
                        ))
                    )}
                </div>
            )}
            <p className="text-xs text-gray-500">
                {selectedIds.length} selected · {filtered.length} shown
                {search.trim() ? ` (filtered from ${domainAssets.length})` : ''}
            </p>
        </div>
    );
};

export default DomainMultiSelect;
